import { Injectable } from '@angular/core';

import { RouterState } from '@angular/router';
import { ModelNameFlags } from '@era/locations-sdk';
import {
  AppLockState,
  BarAppLockConfigurationService,
  BrandAppLockConfigurationService,
  FrontDeskAppLockConfigurationService,
  HousekeepingAppLockConfigurationService,
  KioskAppLockConfigurationService,
  LocationAppLockConfigurationService,
  RoomAppLockConfigurationService,
  StaffAppLockConfigurationService,
} from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, mergeMap } from 'rxjs/operators';
import { LocationsRouterStoreSelectors } from '../router';
import { Category } from '../router/category';
import {
  AppLockApiActions,
  CategoryAppLockResolverActions,
  CategoryAppLockViewActions,
} from './category-app-lock.actions';
import { AppLock, AppLocks } from './category-app-lock.state';
import { safeMap } from '../effects.helpers';

@Injectable()
export class AppLockEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly routeStore: Store<RouterState>,
    private readonly brandApi: BrandAppLockConfigurationService,
    private readonly locationApi: LocationAppLockConfigurationService,
    private readonly roomApi: RoomAppLockConfigurationService,
    private readonly housekeepingApi: HousekeepingAppLockConfigurationService,
    private readonly staffApi: StaffAppLockConfigurationService,
    private readonly frontDeskApi: FrontDeskAppLockConfigurationService,
    private readonly barApi: BarAppLockConfigurationService,
    private readonly kioskApi: KioskAppLockConfigurationService
  ) {}

  /**
   * App lock has been selected for a category.
   * Effect: we add the app lock to the category via the API.
   */
  appLockSelectedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockViewActions.appLockSelected),
      mergeMap((action) => {
        return this.routeStore.select(LocationsRouterStoreSelectors.selectCategory(action.categoryType)).pipe(
          filter((category: Category | undefined): category is Category => !!category),
          first(),
          mergeMap((category: Category) =>
            safeMap(
              this.addAppLockToCategoryEffectBuilder(category, action),
              (appLockConfig) => {
                const appLocks: AppLocks = {
                  bundleIdentifier: action.bundleIdentifier,
                  modelNames: appLockConfig.models,
                  appLockState: action.deviceState,
                  categoryId: category.categoryId,
                  categoryType: category.categoryType,
                };
                return AppLockApiActions.appLockSet({ category, appLocks });
              },
              (error) =>
                AppLockApiActions.appLockSetError({
                  error,
                })
            )
          )
        );
      })
    );
  });

  /**
   * App lock has been removed from a category.
   * Effect: we remove the app lock from the category via the API.
   */
  appLockRemovedFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockViewActions.appLockRemoved),
      mergeMap((action) => {
        return this.routeStore.select(LocationsRouterStoreSelectors.selectCategory(action.categoryType)).pipe(
          filter((category: Category | undefined): category is Category => !!category),
          first(),
          mergeMap((category: Category) =>
            safeMap(
              this.deleteAppLockFromCategoryEffectBuilder(category, action),
              () => {
                const appLocks: AppLocks = {
                  bundleIdentifier: action.bundleIdentifier,
                  modelNames: action.modelNames,
                  appLockState: action.deviceState,
                  categoryId: category.categoryId,
                  categoryType: category.categoryType,
                };
                return AppLockApiActions.appLockRemoved({ category, appLocks });
              },
              (error) =>
                AppLockApiActions.appLockRemovedError({
                  error,
                })
            )
          )
        );
      })
    );
  });

  /**
   * App locks have been requested for a category (resolver).
   * Effect: we load the app locks from the API.
   */
  appLocksForCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockResolverActions.categoryAppLockRequested),
      filter((action) => !!action.category),
      mergeMap((action) => {
        const category = action.category;
        return safeMap(
          this.getAppLocksForCategoryEffectBuilder(category, action.modelNames),
          (locks) => {
            const appLocks = locks.flatMap((lock) => {
              return lock.models.map((lockModelName) => {
                const appLock: AppLock = {
                  bundleIdentifier: lock.bundleIdentifier,
                  modelName: lockModelName,
                  appLockState: lock.appLockState,
                  categoryId: category.categoryId,
                  categoryType: category.categoryType,
                };
                return appLock;
              });
            });
            return AppLockApiActions.appLockLoaded({ category, appLocks });
          },
          (error) => AppLockApiActions.appLockLoadedError({ error })
        );
      })
    );
  });

  /**
   * Generic effect builder for adding app locks to any category type.
   */
  private addAppLockToCategoryEffectBuilder(
    category: Category,
    payload: {
      bundleIdentifier: string;
      modelNames: ModelNameFlags[];
      deviceState?: AppLockState; // optional for room, bar, frontdesk, housekeeping, kiosk, staff
    }
  ): Observable<{ models: ModelNameFlags[] }> {
    const requestPayload = {
      bundleIdentifier: payload.bundleIdentifier,
      models: payload.modelNames,
      appLockState: payload.deviceState,
    };

    switch (category.categoryType) {
      case 'Brand':
        return this.brandApi.addAppLockToBrand(category.categoryId, requestPayload);
      case 'Location':
        return this.locationApi.addAppLockToLocation(category.categoryId, requestPayload);
      case 'Room':
        return this.roomApi.addAppLockToRoom(category.categoryId, {
          bundleIdentifier: payload.bundleIdentifier,
          appLockState: 'Assigned',
          models: payload.modelNames,
        });
      case 'Bar':
        return this.barApi.addAppLockToBar(category.categoryId, {
          bundleIdentifier: payload.bundleIdentifier,
          appLockState: 'Assigned',
          models: payload.modelNames,
        });
      case 'FrontDesk':
        return this.frontDeskApi.addAppLockToFrontDesk(category.categoryId, {
          bundleIdentifier: payload.bundleIdentifier,
          appLockState: 'Assigned',
          models: payload.modelNames,
        });
      case 'Housekeeping':
        return this.housekeepingApi.addAppLockToHousekeeping(category.categoryId, {
          bundleIdentifier: payload.bundleIdentifier,
          appLockState: 'Assigned',
          models: payload.modelNames,
        });
      case 'Kiosk':
        return this.kioskApi.addAppLockToKiosk(category.categoryId, {
          bundleIdentifier: payload.bundleIdentifier,
          appLockState: 'Assigned',
          models: payload.modelNames,
        });
      case 'Staff':
        return this.staffApi.addAppLockToStaff(category.categoryId, {
          bundleIdentifier: payload.bundleIdentifier,
          appLockState: 'Assigned',
          models: payload.modelNames,
        });
    }
  }

  /**
   * Generic effect builder for removing app locks from any category type.
   */
  private deleteAppLockFromCategoryEffectBuilder(
    category: Category,
    payload: {
      modelNames: ModelNameFlags[];
      deviceState?: AppLockState; // optional for room, bar, frontdesk, housekeeping, kiosk, staff
    }
  ): Observable<void> {
    switch (category.categoryType) {
      case 'Brand':
        return this.brandApi.deleteAppLockFromBrand(category.categoryId, payload.deviceState, payload.modelNames);
      case 'Location':
        return this.locationApi.deleteAppLockFromLocation(category.categoryId, payload.deviceState, payload.modelNames);
      case 'Room':
        return this.roomApi.deleteAppLockFromRoom(category.categoryId, payload.modelNames);
      case 'Bar':
        return this.barApi.deleteAppLockFromBar(category.categoryId, payload.modelNames);
      case 'FrontDesk':
        return this.frontDeskApi.deleteAppLockFromFrontDesk(category.categoryId, payload.modelNames);
      case 'Housekeeping':
        return this.housekeepingApi.deleteAppLockFromHousekeeping(category.categoryId, payload.modelNames);
      case 'Kiosk':
        return this.kioskApi.deleteAppLockFromKiosk(category.categoryId, payload.modelNames);
      case 'Staff':
        return this.staffApi.deleteAppLockFromStaff(category.categoryId, payload.modelNames);
    }
  }

  /**
   * Generic effect builder for loading app locks for any category type.
   */
  private getAppLocksForCategoryEffectBuilder(
    category: Category,
    modelNames: ModelNameFlags[]
  ): Observable<{ appLockState: AppLockState; bundleIdentifier: string; models: ModelNameFlags[] }[]> {
    switch (category.categoryType) {
      case 'Brand':
        return this.brandApi.getAppLocksForBrand(category.categoryId, null, null, modelNames);
      case 'Location':
        return this.locationApi.getAppLocksForLocation(category.categoryId, null, null, modelNames);
      case 'Room':
        return this.roomApi.getAppLocksForRoom(category.categoryId, modelNames);
      case 'Bar':
        return this.barApi.getAppLocksForBar(category.categoryId, null, modelNames);
      case 'FrontDesk':
        return this.frontDeskApi.getAppLocksForFrontDesk(category.categoryId, null, modelNames);
      case 'Housekeeping':
        return this.housekeepingApi.getAppLocksForHousekeeping(category.categoryId, null, modelNames);
      case 'Kiosk':
        return this.kioskApi.getAppLocksForKiosk(category.categoryId, null, modelNames);
      case 'Staff':
        return this.staffApi.getAppLocksForStaff(category.categoryId, null, modelNames);
    }
  }
}
